<template>
  <div>
    <v-row style="height: 35px" v-if="!readOnly" class="mb-n1 mt-1">
      <v-spacer />
      <template-editor-actions
        v-if="!isPlain"
        class="mr-4"
        style="z-index: 1"
        :partnerId="partnerId"
        :editor="editor"
        :editConfig="editConfig"
        @click="handleEdit"
        @addPlaceholder="addPlaceholderToBody"
        ref="editorActions"
      >
        <slot />
      </template-editor-actions>
      <v-card v-else class="pr-5" outlined height="30">
        <slot />
      </v-card>
    </v-row>
    <v-row dense>
      <v-col class="pt-0" style="position: relative">
        <!-- label -->
        <div style="position: absolute; height: 0" class="v-text-field__slot">
          <label
            for="form-value"
            style="position: relative; left: 10px; bottom: 11px; z-index: 1; font-size: 12px"
            :style="`${label ? 'padding: 2px;' : ''} background-color: ${
              !$vuetify.theme.dark ? 'white;' : 'rgb(30, 30, 30)'
            };
              `"
            :class="dynamicLabelClasses"
            class="v-label"
          >
            {{ label }}
          </label>
        </div>
        <!-- editor -->
        <v-textarea
          id="form-value"
          v-if="isPlain"
          v-model="templateLocal"
          :readonly="readOnly"
          :outlined="outlined"
          :hint="hint"
          :persistent-hint="persistentHint"
        />
        <editor-content
          id="form-value"
          v-else
          ref="editor"
          :editor="editor"
          :class="dynamicTipTapClasses"
          @mouseover="setMouseOver(true)"
        />
      </v-col>
    </v-row>
    <!-- counter -->
    <v-row dense v-if="!readOnly && !hideCounter">
      <v-col>
        <v-spacer />
        <small :style="bodyCounterStyle"> {{ bodyLength }}/50000 </small>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { MenuActionOptionEnum } from "@/lib/enum/templateEnums/menuActionOptionEnum.enum";
import { MenuStyleOptionEnum } from "@/lib/enum/templateEnums/menuStyleOptionEnum";
import { isHtmlString } from "@/lib/utility/html-helper";
import { ConfigModule } from "@/store/modules/config";
import BulletList from "@tiptap/extension-bullet-list";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import OrderedList from "@tiptap/extension-ordered-list";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { Editor, EditorContent, Extension } from "@tiptap/vue-2";

const PreserveStyles = Extension.create({
  name: "preserveStyles",
  addGlobalAttributes() {
    return [
      {
        types: [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "listItem",
          "image",
          "table",
          "tableRow",
          "tableCell",
          "tableHeader",
          "blockquote",
          "codeBlock",
          "horizontalRule",
          "hardBreak"
        ],
        attributes: {
          style: {
            default: null,
            parseHTML: (el) => el.getAttribute("style"),
            renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {})
          },
          class: {
            default: null,
            parseHTML: (el) => el.getAttribute("class"),
            renderHTML: (attrs) => (attrs.class ? { class: attrs.class } : {})
          }
        }
      }
    ];
  }
});
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Markdown } from "tiptap-markdown";
import Card from "../utility/Card.vue";
import TemplateEditorActions, {
  IActionSpecialItem,
  ITemplateActionConfig,
  ITemplateEditorConfig,
  TemplateActionSpecialItemType
} from "./TemplateEditorActions.vue";

@Component({
  components: { EditorContent, Card, TemplateEditorActions }
})
export default class TemplateEditor extends Vue {
  @Prop({ default: "" })
  value?: string;

  @Prop()
  partnerId?: string;

  @Prop({ default: "150px" })
  height!: string;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: true })
  outlined!: boolean;

  @Prop()
  label?: string;

  @Prop()
  hint?: string;

  @Prop()
  persistentHint?: string;

  @Prop()
  editConfiguration?: TemplateEditConfiguration;

  @Prop()
  isMobile?: boolean;

  @Prop()
  isPlain?: boolean;

  @Prop({ default: false })
  hideCounter?: boolean;

  @Prop({ default: false })
  useMarkdown?: boolean;

  /**
   * If true, pasted plain text that contains html markup (e.g. a full html email template copied
   * from a code editor) is parsed as html instead of being inserted as escaped text.
   * Formatting that the editor does not support is stripped in the process.
   */
  @Prop({ default: false })
  parsePastedHtml?: boolean;

  /**
   * If true, a non empty value is emitted back in the representation of the editor as soon as it is
   * mounted. Use it when the value may come from another editor (e.g. raw html) to make sure that
   * what is displayed is also what is stored.
   */
  @Prop({ default: false })
  normalizeOnMount?: boolean;

  get dynamicLabelClasses() {
    return this.getDynamicStyleClasses("Label");
  }

  get dynamicTipTapClasses() {
    const classes: string = this.getDynamicStyleClasses("TipTap");

    return classes;
  }

  getDynamicStyleClasses(suffix: string) {
    const classes: string[] = [];

    if (this.outlined) {
      classes.push("border" + suffix);
      const prefix = ConfigModule.darkMode ? "dark" : "light";
      const name = this.isFocused ? "Focused" : this.isMouseOver ? "Hover" : "Outline";
      classes.push(`${prefix}${name}${suffix}`);
    } else {
      classes.push("noBorder" + suffix);
    }

    return classes.join(" ");
  }

  get templateLocal() {
    return this.value;
  }
  set templateLocal(t) {
    this.$emit("input", t);
  }

  get bodyLength() {
    return this.templateLocal ? this.templateLocal.length : 0;
  }

  get MenuStyleOptionEnum() {
    return MenuStyleOptionEnum;
  }

  get MenuActionOptionEnum() {
    return MenuActionOptionEnum;
  }

  get bodyCounterStyle() {
    const color = (this.templateLocal?.length || 0) > 50000 ? "orange" : "info";

    return `color: ${color}`;
  }

  get editConfig() {
    const editConfiguration = this.editConfiguration || new TemplateEditConfiguration(this.partnerId);

    return editConfiguration.getConfig(this.isMobile ?? this.$vuetify.breakpoint.smAndDown);
  }

  showCode = false;

  editor: null | Editor = null;

  handleEdit(editConfig?: ITemplateActionConfig) {
    if (editConfig?.content) {
      this.executeAction(editConfig?.content, editConfig?.param);
    }
  }

  executeAction(menuOption: MenuStyleOptionEnum | MenuActionOptionEnum, param?: object) {
    if (!this.editor) {
      return;
    }

    const action: string = menuOption;
    if (menuOption === MenuStyleOptionEnum.SetImage) {
      (this.$refs.editorActions as TemplateEditorActions).startImageUpload((url) => {
        this.editor?.chain().focus()[action]({ src: url }).run();
      });
      return;
    }
    if (menuOption === MenuStyleOptionEnum.SetLink) {
      (this.$refs.editorActions as TemplateEditorActions).startUrlUpload((url) => {
        this.editor?.chain().focus()[action]({ href: url }).run();
      });
      return;
    }

    this.editor
      .chain()
      .focus()
      [menuOption](param as any)
      .run();
  }

  mounted() {
    // this.randomEditorUUId = uniqueId();

    const extensions = [
      StarterKit,
      Highlight.configure({ multicolor: true }),
      Image,
      Link.configure({ autolink: false }),
      OrderedList,
      BulletList,
      Underline,
      PreserveStyles
    ];

    if (this.useMarkdown) {
      extensions.push(Markdown as any);
    }

    this.editor = new Editor({
      content: this.templateLocal,
      extensions,
      editorProps: this.parsePastedHtml ? { handlePaste: this.handlePaste } : {},
      onUpdate: this.onUpdate,
      editable: !this.readOnly,
      onFocus: () => this.setFocused(true),
      onBlur: () => this.setFocused(false)
    });

    if (this.normalizeOnMount && this.templateLocal && !this.isPlain) {
      const normalized = this.useMarkdown ? this.editor.storage.markdown.getMarkdown() : this.editor.getHTML();
      if (normalized !== this.templateLocal) this.templateLocal = normalized;
    }

    const editor = this.$refs.editor as Vue;
    editor?.$el?.addEventListener("mouseenter", () => this.setMouseOver(true));
    editor?.$el?.addEventListener("mouseleave", () => this.setMouseOver(false));
  }
  setMouseOver(isMouseOver: boolean) {
    this.isMouseOver = isMouseOver;
  }
  isMouseOver = false;

  setFocused(isFocused: boolean) {
    this.isFocused = isFocused;
  }
  isFocused = false;

  /**
   * Can be used to lock or unlock the editor
   * @param isEditable
   */
  @Watch("isEditable")
  updateLock(isEditable: boolean) {
    this.editor?.setEditable(isEditable);
  }

  beforeDestroy() {
    const editor = this.$refs.editor as Vue;
    editor?.$el?.removeEventListener("mouseenter", () => this.setMouseOver(true));
    editor?.$el?.removeEventListener("mouseleave", () => this.setMouseOver(false));

    this.editor?.destroy();
  }

  onUpdate(arg: any) {
    if (!arg.editor) {
      return;
    }

    this.templateLocal = this.useMarkdown ? arg.editor.storage.markdown.getMarkdown() : arg.editor.getHTML();
  }

  /**
   * Parses pasted html source code instead of inserting it as text.
   *
   * The editor already handles clipboard content that carries a `text/html` flavour (e.g. a copied
   * rendered mail). Html that is copied from a code editor only carries `text/plain` though, so
   * without this the markup would be escaped and shown as code.
   */
  handlePaste(_view: unknown, event: ClipboardEvent) {
    if (!this.editor) return false;

    // let the editor handle clipboard content that already is html
    if (event.clipboardData?.getData("text/html")) return false;

    const text = event.clipboardData?.getData("text/plain") ?? "";
    if (!isHtmlString(text)) return false;

    event.preventDefault();
    this.editor.commands.insertContent(text, { parseOptions: { preserveWhitespace: false } });

    return true;
  }

  addPlaceholderToBody(placeholder: string) {
    if (!this.editor) {
      return;
    }

    this.editor.commands.insertContent(placeholder, {
      parseOptions: { preserveWhitespace: true }
    });
  }

  clear() {
    this.templateLocal = "";
    this.editor?.commands.clearContent();
  }
}

export class TemplateEditConfiguration {
  constructor(
    private readonly partnerId?: string,
    private readonly isPlaceholderOption = false
  ) {}
  private readonly colors = [
    "#424242",
    "#4283FF",
    "#3BC8C1",
    "#4caf50",
    "#81C784",
    "#FF9800",
    "#FFB74D",
    "#f44336",
    "#7E57C2",
    "#9e9e9e"
  ];
  private readonly placeholder: IActionSpecialItem = { item: TemplateActionSpecialItemType.ADD_PLACEHOLDER };

  private readonly actions: ITemplateEditorConfig[] = [
    {
      content: MenuActionOptionEnum.Undo,
      display: {
        icon: "mdi-undo"
      }
    },
    {
      content: MenuActionOptionEnum.Redo,
      display: {
        icon: "mdi-redo"
      }
    }
  ];

  private readonly fontStyleOptions: ITemplateEditorConfig[] = [
    {
      content: MenuStyleOptionEnum.Bold,
      display: {
        icon: "mdi-format-bold"
      }
    },
    {
      content: MenuStyleOptionEnum.Italic,
      display: {
        icon: "mdi-format-italic"
      }
    },
    {
      content: MenuStyleOptionEnum.Strike,
      display: {
        icon: "mdi-format-strikethrough"
      }
    },
    {
      content: MenuStyleOptionEnum.Underline,
      display: {
        icon: "mdi-format-underline"
      }
    },
    {
      content: MenuStyleOptionEnum.SetLink,
      display: {
        icon: "mdi-link-variant"
      }
    }
  ];

  private readonly listOptions: ITemplateEditorConfig[] = [
    {
      content: MenuStyleOptionEnum.BulletList,
      display: {
        icon: "mdi-format-list-bulleted"
      }
    },
    {
      content: MenuStyleOptionEnum.OrderedList,
      display: {
        icon: "mdi-format-list-numbered"
      }
    }
    // {
    //   content: MenuStyleOptionEnum.TaskList,
    //   display: {
    //     icon: "mdi-format-list-checks"
    //   }
    // }
  ];

  private readonly headingOptions: ITemplateEditorConfig[] = [
    {
      sublist: [
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 1 },
          display: {
            icon: "mdi-format-header-1"
          }
        },
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 2 },
          display: {
            icon: "mdi-format-header-2"
          }
        },
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 3 },
          display: {
            icon: "mdi-format-header-3"
          }
        },
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 4 },
          display: {
            icon: "mdi-format-header-4"
          }
        },
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 5 },
          display: {
            icon: "mdi-format-header-5"
          }
        },
        {
          content: MenuStyleOptionEnum.Heading,
          param: { level: 6 },
          display: {
            icon: "mdi-format-header-6"
          }
        }
      ],
      display: {
        icon: "mdi-format-header-pound"
      }
    }
  ];

  private insertionOptions: ITemplateEditorConfig = ((partnerId?: string) => {
    const image = {
      content: MenuStyleOptionEnum.SetImage,
      display: {
        icon: "mdi-image-plus"
      }
    };

    const placeholder = this.placeholder;

    const insertionOptions: ITemplateEditorConfig = {
      sublist: [],
      display: {
        icon: "mdi-plus-thick"
      }
    };

    if (partnerId) {
      insertionOptions.sublist.push(image);
    }

    if (this.isPlaceholderOption) {
      insertionOptions.sublist.push(placeholder);
    }

    return insertionOptions;
  })(this.partnerId);

  private readonly highLightOptions: ITemplateEditorConfig = (() => {
    const highlightColor: ITemplateEditorConfig = {
      sublist: [],
      display: {
        icon: "mdi-marker"
      }
    };
    for (const color of this.colors) {
      highlightColor?.sublist?.push({
        content: MenuStyleOptionEnum.ToggleHighlight,
        display: {
          color
        },
        param: { color }
      });
    }
    highlightColor?.sublist?.push({
      content: MenuStyleOptionEnum.UnsetHighlight,
      display: {
        icon: "mdi-format-color-marker-cancel"
      }
    });

    return highlightColor;
  })();

  getConfig(isMobile?: boolean): ITemplateEditorConfig[] {
    if (isMobile) {
      return [
        ...this.actions,
        {
          sublist: [...this.fontStyleOptions],
          display: {
            icon: "mdi-format-text"
          }
        },
        {
          sublist: [...this.headingOptions],
          display: {
            icon: "mdi-format-line-style"
          }
        },
        {
          sublist: [this.highLightOptions],
          display: {
            icon: "mdi-invert-colors"
          }
        },
        {
          sublist: [this.insertionOptions, ...this.listOptions],
          display: {
            icon: "mdi-format-float-right"
          }
        }
      ];
    } else {
      return [
        ...this.actions,
        { item: TemplateActionSpecialItemType.LINE },
        ...this.headingOptions,
        ...this.fontStyleOptions,
        this.highLightOptions,
        { item: TemplateActionSpecialItemType.LINE },
        ...this.listOptions,
        { item: TemplateActionSpecialItemType.LINE },
        this.insertionOptions
      ];
    }
  }
}
</script>

<style scoped>
.borderTipTap {
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  padding: 1px;
  padding-bottom: -2px;
  height: 100%;
  width: 100%;
}
.darkOutlineTipTap {
  border-color: #545454;
}
.darkOutlineLabel {
  color: rgba(255, 255, 255, 0.7);
}
div.darkMode .darkOutlineLabel {
  color: rgba(255, 255, 255, 0.7);
}
.darkHoverTipTap {
  border-color: #ffffff;
}
.darkHoverLabel {
  color: #ffffff;
}
.lightOutlineTipTap {
  border-color: rgba(0, 0, 0, 0.38);
}
.lightOutlineLabel {
  color: rgb(103, 103, 103);
}
.lightHoverTipTap {
  border-color: #000000;
}
.lightHoverLabel {
  color: rgb(103, 103, 103);
}
.darkFocusedTipTap {
  border-width: 2px;
  padding: 0px;
  border-color: #418ef5;
}
.darkFocusedLabel {
  color: #418ef5;
}
.lightFocusedTipTap {
  border-width: 2px;
  padding: 0px;
  border-color: #418ef5 !important;
}
.lightFocusedLabel {
  color: #418ef5 !important;
}
</style>

<style>
.ProseMirror:focus {
  outline: none;
  /* margin-bottom: -12px; */
}
.ProseMirror {
  margin-right: -12px;
  margin-left: -12px;
  overflow: scroll;
  min-height: 200px;
}
.ProseMirror > p {
  line-height: 1;
  margin-bottom: 5px;
  font-size: 1rem;
}
.ProseMirror > h1 {
  line-height: 2;
  font-size: 2em;
  font-weight: bolder;
}
.ProseMirror > h2 {
  line-height: 1.5;
  font-size: 1.5em;
  font-weight: bolder;
}
.ProseMirror > h3 {
  line-height: 1.17;
  font-size: 1.17em;
  font-weight: bolder;
}
.ProseMirror > h4 {
  line-height: 1;
  font-size: 1em;
  font-weight: bolder;
}
.ProseMirror > h5 {
  line-height: 0.83;
  font-size: 0.83em;
  font-weight: bolder;
}
.ProseMirror > h6 {
  line-height: 0.67;
  font-size: 0.67em;
  font-weight: bolder;
}
div.ProseMirror {
  margin: 2px 2px 1px 2px;
  padding: 12px 8px 25px 8px;
}
</style>
